import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  Bot,
  KeyRound,
  LayoutDashboard,
  LineChart,
  LogOut,
  ShieldCheck,
  Loader2,
  Hammer,
  BarChart3,
  Settings,
  Crown,
  Users,
  Spline,
  Magnet,
  Send,
  Mail,
  Workflow,
  Inbox,
  Target,
  Activity,
  Hourglass,
  Cpu,
  CalendarClock,
  PackageOpen,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { crmApi } from "@/lib/ownerClient";

const SECTIONS = [
  { to: "/admin/owner", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/owner/library", label: "Sales Library", icon: BookOpen },
  { to: "/admin/owner/agents", label: "Agents", icon: Bot },
  { to: "/admin/owner/projects", label: "Projects", icon: Hammer },
  { to: "/admin/owner/plans", label: "Plans", icon: Crown },
  { to: "/admin/owner/members", label: "Members", icon: Users },
  { to: "/admin/owner/dispatches", label: "Dispatches", icon: Spline },
  { to: "/admin/owner/attach", label: "Attach", icon: Magnet },
  { to: "/admin/owner/leads", label: "Leads", icon: Inbox },
  { to: "/admin/owner/prospects", label: "Prospects", icon: Target },
  { to: "/admin/owner/waitlist", label: "Waitlist (July)", icon: Hourglass },
  { to: "/admin/owner/swarm", label: "Swarm", icon: Cpu },
  { to: "/admin/owner/calendar", label: "Calendar", icon: CalendarClock },
  { to: "/admin/owner/metrics", label: "Campaign Metrics", icon: BarChart3 },
  { to: "/admin/owner/activity", label: "24/7 Activity", icon: Activity },
  { to: "/admin/owner/outbound", label: "Outbound", icon: Send },
  { to: "/admin/owner/templates", label: "Templates", icon: Mail },
  { to: "/admin/owner/sequences", label: "Sequences", icon: Workflow },
  { to: "/admin/owner/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/owner/vault", label: "Owner Vault", icon: KeyRound },
  { to: "/admin/owner/audit", label: "Compliance Audit", icon: ShieldCheck },
  { to: "/admin/owner/pipeline", label: "Pipeline Health", icon: LineChart },
  { to: "/admin/owner/settings", label: "Tenant Settings", icon: Settings },
  { to: "/admin/owner/handoff", label: "Handoff Bundle", icon: PackageOpen },
];

export default function OwnerLayout() {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const me = await crmApi.me();
        setCtx(me);
      } catch (exc) {
        if (exc.status === 401) {
          navigate("/admin/login?next=/admin/owner", { replace: true });
          return;
        }
        setError(exc.message || "Failed to load CRM context");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="dark min-h-screen bg-black text-white grid place-items-center">
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading CRM…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dark min-h-screen bg-black text-white grid place-items-center px-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto" />
          <p className="text-white">{error}</p>
          <Link to="/admin" className="underline text-amber-300">Back to legacy admin</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-black text-white" data-testid="owner-shell">
      <div className="border-b border-zinc-800 bg-black/80 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-black font-black text-sm">BR</div>
            <div>
              <div className="text-sm font-semibold tracking-wide text-white">Black Ridge CRM</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Phase A · Foundation</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-white">
            <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid="owner-role-badge">
              {ctx?.role || "owner"}
            </Badge>
            <span className="hidden sm:inline text-zinc-400">{ctx?.tenant?.display_name || ctx?.tenant_id}</span>
            <Link to="/admin" className="hidden sm:inline text-zinc-400 hover:text-white underline-offset-4 hover:underline">
              legacy admin
            </Link>
            <Button asChild variant="ghost" size="sm" className="text-white hover:text-amber-300">
              <Link to="/admin/login" data-testid="owner-logout-link">
                <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="space-y-1" data-testid="owner-sidenav">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              end={s.end}
              data-testid={`owner-nav-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                    : "text-white hover:bg-zinc-900/70 border border-transparent"
                }`
              }
            >
              <s.icon className="h-4 w-4" />
              {s.label}
            </NavLink>
          ))}
        </aside>

        <main className="min-w-0">
          <Outlet context={{ ctx }} />
        </main>
      </div>
    </div>
  );
}
